import { getInitials } from "@upsquad/shared";
import { Link as LinkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getUser } from "@/src/auth/store/auth-selectors";
import { Avatar, Button } from "@/src/common/components/ui";
import { buildCommunityUrl } from "@/src/common/lib/subdomain";
import { useAppSelector } from "@/src/common/redux/store";
import { toastService } from "@/src/common/services/toast.service";

interface CommunityPreviewCardProps {
	name: string;
	slug: string;
	imageUrl?: string | null;
}

function extractDisplayUrl(fullUrl: string): string {
	return fullUrl.replace(/^https?:\/\//, "");
}

export function CommunityPreviewCard({ name, slug, imageUrl }: CommunityPreviewCardProps) {
	const { t } = useTranslation("onboarding");
	const user = useAppSelector(getUser);
	const fullUrl = slug ? buildCommunityUrl(slug) : "";
	const displayUrl = slug ? extractDisplayUrl(fullUrl) : "";
	const displayName = name || "Community Name";

	const handleCopyLink = () => {
		navigator.clipboard.writeText(fullUrl).catch(() => {
			toastService.error("Failed to copy");
		});
		toastService.success(t("link_copied"));
	};

	return (
		<div className="bg-white/90 backdrop-blur rounded-2xl p-6 shadow-xl max-w-sm w-full">
			<div className="flex items-start justify-between mb-4">
				<h3 className="text-2xl font-bold text-gray-900 leading-tight">{displayName}</h3>
				<Avatar size="lg">
					<Avatar.Image src={imageUrl ?? undefined} alt={displayName} />
					<Avatar.Fallback>{getInitials(displayName)}</Avatar.Fallback>
				</Avatar>
			</div>
			<div className="mb-4">
				<p className="text-sm text-gray-500">{t("created_by")}</p>
				<p className="font-semibold text-gray-900">
					{user?.first_name ?? ""} {user?.last_name ?? ""}
				</p>
				<p className="text-sm text-gray-500">{user?.email}</p>
			</div>
			<div>
				<p className="text-sm text-gray-500 mb-2">{t("community_url")}</p>
				<div className="flex items-center gap-2 bg-gray-100 rounded-lg p-3">
					<LinkIcon className="w-4 h-4 text-gray-400 shrink-0" />
					<span className="text-sm text-gray-700 flex-1 truncate">{displayUrl}</span>
					<Button size="sm" variant="primary" onPress={handleCopyLink} className="shrink-0">
						{t("copy_link")}
					</Button>
				</div>
			</div>
		</div>
	);
}
